import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import { useForm } from 'react-hook-form';
import moment from 'moment';
import ReactTooltip from 'react-tooltip';
import { withRouter } from 'react-router';
import { Link } from 'react-router-dom';

import { bookingShape, userShape, historyShape, customerInfo as customerInfoShape } from 'constants/propTypes';
import { NO_VAT_PRODUCT_CODE } from 'constants/booking';
import { isThalysAdminUser, isThalysAdminOrSuperUser } from 'utils/user';
import Modal from 'components/ui/Modal';

import { Column } from 'components/shared/Layout';
import { getBookingSearchURL, getPendingPaymentURL } from 'utils/urls';
import {
  getDateObjects,
  getVATStatus,
  enableAfterSalesBasedOnConfirmedTimeStamp,
  findSubByReferenceOrStatus,
  getDiscountCode
} from '../../BookingDetails/Extras/ExtrasSubscriptions/ExtrasSubscriptions.utils';
import { VAT_ADDITIONAL_DETAILS_KEYS } from '../../../constants/booking';
import { TYPE_OF_USE } from '../../../types/constants';
import { CancellationModals } from './Cancellation/CancellationModals/CancellationModals';

import {
  CtaButton,
  FormContainer,
  StyledLabel,
  StyledTextInput,
  StyledDescription,
  StyledSelect,
  StyledRow,
  StyledLinkableTextInput
} from './SubscriptionDetails.styles';
import { SubscriptionStatus, SubscriptionStatusCode } from 'constants/subscriptions';
import PaymentsSummary from 'components/shared/PaymentsSummary';
import { ExtendSubscription } from './Extension/ExtendSubscription';
import { ChangeStartDate } from './Extension/ChangeStartDate';
import CancelBeforePayment from './Cancellation/CancelBeforePayment';
import RefundVAT from './Cancellation/RefundVAT';
import Bookings from './Bookings';
import CurrencySymbol from 'components/ui/CurrencySymbol';
import { isLoungeStaff } from 'utils/user';

const DATE_FORMAT = 'DD-MM-YYYY';
const DATE_TIME_DISPLAY_FORMAT = 'ddd Do MMM YYYY, HH:mm';

const SubscriptionDetails = ({ customerInfo, subscriptionData, intl, history, user, canSellSubscription }) => {
  const selectReference = new URLSearchParams(history.location.search).get('reference');
  const isLoungeStaffUser = isLoungeStaff(user);
  const userCanCancelSubscription = isThalysAdminOrSuperUser(user);
  const [extend, setExtend] = useState(false);
  const [refundVAT, setRefundVAT] = useState(false);
  const [changeStartDate, setChangeStartDate] = useState(false);
  const isThalysAdminUserOffice = isThalysAdminUser(user);
  const [showEnterReason, setShowEnterReason] = useState(false);
  const [cancelBeforePayment, setCancelBeforePayment] = useState(false);
  const [subscription, setSubscription] = useState(
    findSubByReferenceOrStatus({ selectReference, subscriptions: subscriptionData }) || subscriptionData[0] || null
  );

  const { daysDiffBetweenTodayAndBookingCreation } = getDateObjects(
    subscription.booking.confirmedTimestamp,
    subscription.endDate,
    subscription.startDate
  );
  const bookingWithin14DaysCoolingOffPeriod = daysDiffBetweenTodayAndBookingCreation <= 14;
  const { register } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      subscription: subscription?.bookingReference
    }
  });

  const getSubOptionName = (subData) => {
    const sub = subData.booking.subscriptions[0];
    const isActive = subData.status === SubscriptionStatusCode.ACTIVE;
    const isCancelled = subData.status === SubscriptionStatusCode.CANCELLED;
    let name = isCancelled
      ? `${sub.name} - ${SubscriptionStatusCode.CANCELLED}`
      : `${sub.name} (${moment(subData.startDate).format(DATE_FORMAT)} - ${moment(subData.endDate).format(DATE_FORMAT)})`;
    if (isActive) {
      name += ' - ACTIVE';
    }

    return name;
  };

  const getTransferDueDateTooltipData = () => `
        <h4>${intl.formatMessage({ id: 'subscriptions.putOnHoldNoDeposit' })}</h4>
        ${intl.formatMessage({ id: 'subscriptions.tooltip.description' })} <br />
        ${intl.formatMessage({ id: 'subscriptions.tooltip.iban' })}: GB56RBOS16106503200158 <br />
        ${intl.formatMessage({ id: 'subscriptions.tooltip.bic' })}: RBOSGB2L <br />
        ${intl.formatMessage({ id: 'subscriptions.tooltip.amount' })}: ${parseFloat(subscription?.booking?.subscriptions[0].price).toFixed(
    2
  )} ${subscription?.booking?.subscriptions[0].currency} <br />
        ${intl.formatMessage({ id: 'paymentLink.reference' })}: ${subscription?.bookingReference} <br />`;

  const isBookingEnabled = [SubscriptionStatusCode.ACTIVE, SubscriptionStatusCode.NOT_STARTED].includes(subscription.status);
  const isConfirmPaymentEnabled = canSellSubscription && subscription.status === SubscriptionStatusCode.PENDING_PAYMENT;
  const isChangeStartDateEnabled =
    canSellSubscription &&
    [SubscriptionStatusCode.NOT_STARTED, SubscriptionStatusCode.PENDING_PAYMENT].includes(subscription.status) &&
    userCanCancelSubscription;

  const isCancelRefundEnabled =
    canSellSubscription &&
    [SubscriptionStatusCode.PENDING_PAYMENT, SubscriptionStatusCode.NOT_STARTED, SubscriptionStatusCode.ACTIVE].includes(
      subscription.status
    ) &&
    enableAfterSalesBasedOnConfirmedTimeStamp({
      status: subscription.status,
      confirmedTimeStamp: subscription?.booking?.confirmedTimestamp
    }) &&
    !moment().isAfter(moment(subscription.endDate)) &&
    userCanCancelSubscription;

  const nonCancelledSubscription = subscription.booking.travelPassProducts.find(({ cancelled }) => !cancelled);
  const vatAlreadyRefunded = subscription.booking.subscriptionVATHasBeenCancelled === true;
  const isNoVATProduct = nonCancelledSubscription?.code?.includes(NO_VAT_PRODUCT_CODE);
  const isRefundVATDisabled = isNoVATProduct || vatAlreadyRefunded;
  const isRefundVATEnabled =
    canSellSubscription &&
    [SubscriptionStatusCode.NOT_STARTED, SubscriptionStatusCode.ACTIVE].includes(subscription.status) &&
    !isRefundVATDisabled;
  const hasNotStartedSubscriptions = !!subscriptionData.find((s) => s.status === SubscriptionStatusCode.NOT_STARTED);
  const isExtendEnabled =
    canSellSubscription &&
    subscription.status === SubscriptionStatusCode.ACTIVE &&
    userCanCancelSubscription &&
    !hasNotStartedSubscriptions;
  // const isTransferEnabled = canSellSubscription && subscription.status === SubscriptionStatusCode.ACTIVE;
  const typeOfUse = subscription.booking?.customer?.companyName ? TYPE_OF_USE.BUSINESS : TYPE_OF_USE.PERSONAL;
  const vatNumber = subscription.booking?.additionalDetails?.find(({ key }) => key === VAT_ADDITIONAL_DETAILS_KEYS.VAT_NUMBER)?.value;
  const vatStatusValue = getVATStatus({ nonCancelledSubscription, vatAlreadyRefunded, isNoVATProduct, intl });
  const discountCode = getDiscountCode({ booking: subscription.booking });

  const onConfirm = () => {
    let paymentsUrl = getPendingPaymentURL(
      subscription.booking.reference,
      subscription.booking.currency,
      customerInfo.profile?.loyalty?.membershipNumber
    );

    paymentsUrl += `&isSubscription=true`;

    history.push(paymentsUrl);
  };

  const onBook = () => {
    const bookingSearchURL = getBookingSearchURL(
      customerInfo.profile?.loyalty?.membershipNumber,
      subscription.travelPassReference,
      subscription.code
    );
    history.push(bookingSearchURL);
  };

  const handleCancelClick = () => {
    subscription.status === SubscriptionStatusCode.PENDING_PAYMENT ? setCancelBeforePayment(true) : setShowEnterReason(true);
  };

  const handleRefundVATClick = () => {
    setRefundVAT(true);
  };

  return (
    <>
      <StyledRow columns={16}>
        <Column span={12}>
          <StyledSelect
            {...register('subscription', {
              required: true,
              onChange: (event) => {
                const ref = event.target.value;
                setSubscription(subscriptionData.find((s) => s.bookingReference === ref));
              }
            })}
            id="subscription"
            data-cy="subscription"
          >
            {subscriptionData.map((sub) => (
              <option key={sub.bookingReference} value={sub.bookingReference}>
                {getSubOptionName(sub)}
              </option>
            ))}
          </StyledSelect>
        </Column>

        <Column span={4}>
          <CtaButton disabled={!isBookingEnabled || isLoungeStaffUser} onClick={onBook}>
            <FormattedMessage id="clubEurostar.bookWithSub" />
          </CtaButton>
        </Column>
      </StyledRow>

      <StyledRow columns={16}>
        <Column span={12}>
          <h4>
            <FormattedMessage id="subscriptions.details" />
          </h4>

          <FormContainer>
            <StyledRow columns={12}>
              <Column span={4}>
                <StyledLabel htmlFor="status">
                  <FormattedMessage id="subscriptions.status" />
                </StyledLabel>
                <StyledTextInput
                  name="status"
                  type="text"
                  value={subscription && SubscriptionStatus[subscription.status]}
                  readOnly
                  data-cy="subscription-status"
                />
              </Column>

              <Column span={4}>
                <StyledLabel htmlFor="id">
                  <FormattedMessage id="subscriptions.id" />
                </StyledLabel>
                <StyledTextInput name="id" type="text" value={subscription?.travelPassReference ?? ''} readOnly data-cy="subscription-id" />
              </Column>

              <Column span={4}>
                <StyledLabel htmlFor="pnr">
                  <FormattedMessage id="subscriptions.pnr" />
                </StyledLabel>
                <StyledLinkableTextInput
                  to={`/manage/bookings/${subscription?.bookingReference}/journeys`}
                  name="pnr"
                  type="text"
                  value={subscription?.bookingReference}
                  readOnly
                  data-cy="subscription-associated-pnr"
                />
              </Column>
            </StyledRow>

            <StyledRow columns={12}>
              <Column span={4}>
                <StyledLabel htmlFor="startDate">
                  <FormattedMessage id="subscriptions.startDate" />
                </StyledLabel>
                <StyledTextInput
                  name="startDate"
                  type="text"
                  value={moment(subscription?.startDate).format(DATE_FORMAT)}
                  readOnly
                  data-cy="subscription-start-date"
                />
              </Column>

              <Column span={4}>
                <StyledLabel htmlFor="endDate">
                  <FormattedMessage id="subscriptions.endDate" />
                </StyledLabel>
                <StyledTextInput
                  name="endDate"
                  type="text"
                  value={moment(subscription?.endDate).format(DATE_FORMAT)}
                  readOnly
                  data-cy="subscription-end-date"
                />
              </Column>

              <Column span={4}>
                <StyledLabel htmlFor="passenger">
                  <FormattedMessage id="subscriptions.passenger" />
                </StyledLabel>
                <StyledTextInput
                  name="passenger"
                  type="text"
                  value={`${subscription?.booking?.passengers?.[0]?.firstName} ${subscription?.booking?.passengers?.[0]?.lastName}`}
                  readOnly
                  data-cy="subscription-passenger"
                />
              </Column>
            </StyledRow>

            <StyledRow columns={12}>
              <Column span={4}>
                <StyledLabel htmlFor="createdBy">
                  <FormattedMessage id="subscriptions.createdBy" />
                </StyledLabel>
                <StyledTextInput
                  name="createdBy"
                  type="text"
                  value={subscription?.booking?.agent?.username}
                  readOnly
                  data-cy="subscription-created-by"
                />
              </Column>

              <Column span={4}>
                <StyledLabel htmlFor="createdOn">
                  <FormattedMessage id="subscriptions.createdOn" />
                </StyledLabel>
                <StyledTextInput
                  name="createdOn"
                  type="text"
                  value={moment(subscription?.booking?.createTimestamp).format(DATE_FORMAT)}
                  readOnly
                  data-cy="subscription-created-on"
                />
              </Column>

              <Column span={4}>
                <StyledLabel htmlFor="confirmedOn">
                  <FormattedMessage id="subscriptions.activatedOn" />
                </StyledLabel>
                <StyledTextInput
                  name="confirmedOn"
                  type="text"
                  value={subscription?.booking?.confirmedTimestamp && moment(subscription?.booking.confirmedTimestamp).format(DATE_FORMAT)}
                  readOnly
                  data-cy="subscription-confirmed-on"
                />
              </Column>

              {/* VOY-1772: Hide for now
              Currently the check for cancelled subscription is done at the booking level in customer-profile
              this need to check for the cancellation of the link subscription of the booking instead */}

              {/* <Column span={3}>
                <StyledLabel htmlFor="cancelledOn">
                  <FormattedMessage id="subscriptions.cancelledOn" />
                </StyledLabel>
                <StyledTextInput
                  name="cancelledOn"
                  type="text"
                  value={subscription?.booking?.cancelledTimestamp && moment(subscription?.booking.cancelledTimestamp).format(DATE_FORMAT)}
                  readOnly
                  data-cy="subscription-cancelled-on"
                />
              </Column> */}
            </StyledRow>
          </FormContainer>
        </Column>

        <Column span={4}>
          <h4>
            <FormattedMessage id="subscriptions.manageSubscription" />
          </h4>

          <CtaButton disabled={!isConfirmPaymentEnabled || isLoungeStaffUser} onClick={onConfirm}>
            <FormattedMessage id="subscriptions.confirmPaymentMethod" />
          </CtaButton>

          <CtaButton
            disabled={!isChangeStartDateEnabled || !nonCancelledSubscription || isLoungeStaffUser}
            onClick={() => setChangeStartDate(true)}
            data-cy={'change-start-date'}
          >
            <FormattedMessage id="subscriptions.changeStartDate" />
          </CtaButton>

          <>
            <div
              data-for="subscriptions.cancelRefund-tooltip"
              data-tip={intl.formatMessage({ id: 'thalyspass.oneDayAfterConfirmedTimestamp' })}
            >
              <CtaButton
                disabled={!isCancelRefundEnabled || !nonCancelledSubscription || isLoungeStaffUser}
                onClick={handleCancelClick}
                data-cy="cancel-button"
                data-testid="subscriptions.cancelRefund"
              >
                <FormattedMessage id="subscriptions.cancelRefund" />
              </CtaButton>
            </div>
            {[SubscriptionStatusCode.NOT_STARTED, SubscriptionStatusCode.ACTIVE].includes(subscription.status) &&
              !enableAfterSalesBasedOnConfirmedTimeStamp({
                status: subscription.status,
                confirmedTimeStamp: subscription?.booking?.confirmedTimestamp
              }) && <ReactTooltip id="subscriptions.cancelRefund-tooltip" data-cy="subscriptions.cancelRefund-tooltip" />}
          </>

          <CtaButton
            disabled={!isRefundVATEnabled || !nonCancelledSubscription || isLoungeStaffUser}
            onClick={handleRefundVATClick}
            data-cy="refund-vat-button"
          >
            <FormattedMessage id="subscriptions.refundVAT" />
          </CtaButton>

          <CtaButton
            data-cy="subscriptions.extend"
            disabled={!isExtendEnabled || !nonCancelledSubscription || isLoungeStaffUser}
            onClick={() => setExtend(true)}
          >
            <FormattedMessage id="subscriptions.extend" />
          </CtaButton>

          {/* TODO: Hidden for the moment. We will bring it back when we work on the feature (VOY-1474) */}
          {/*<CtaButton disabled={!isTransferEnabled}>*/}
          {/*  <FormattedMessage id="subscriptions.transfer" />*/}
          {/*</CtaButton>*/}
        </Column>
      </StyledRow>

      {refundVAT && (
        <RefundVAT
          onClose={() => setRefundVAT(false)}
          bookingReference={subscription?.bookingReference}
          currency={subscription.booking.currency}
          membershipNumber={customerInfo.profile?.loyalty?.membershipNumber}
          history={history}
          travelPassPrice={nonCancelledSubscription?.price}
          itemRef={nonCancelledSubscription?.itemRef}
        />
      )}

      {changeStartDate && (
        <ChangeStartDate
          onClose={() => setChangeStartDate(false)}
          travelStartDate={subscription?.startDate}
          travelDateEnd={subscription?.endDate}
          travelPassReference={subscription?.travelPassReference}
          code={subscription.code}
          status={subscription.status}
          bookingReference={subscription?.bookingReference}
          membershipNumber={customerInfo.profile?.loyalty?.membershipNumber}
        />
      )}

      {cancelBeforePayment && (
        <CancelBeforePayment
          onClose={() => setCancelBeforePayment(false)}
          bookingReference={subscription?.bookingReference}
          currency={subscription.booking.currency}
          membershipNumber={customerInfo.profile?.loyalty?.membershipNumber}
          history={history}
        />
      )}

      {extend && (
        <ExtendSubscription
          onClose={() => setExtend(false)}
          travelDateEnd={subscription?.endDate}
          travelPassReference={subscription?.travelPassReference}
          code={subscription.code}
          bookingReference={subscription?.bookingReference}
          membershipNumber={customerInfo.profile?.loyalty?.membershipNumber}
        />
      )}

      {showEnterReason && (
        <Modal isOpen={showEnterReason} title={<FormattedMessage id="paymentsTab.calcRefund" />} onClose={() => setShowEnterReason(false)}>
          <CancellationModals
            onClose={() => setShowEnterReason(false)}
            itemRef={nonCancelledSubscription?.itemRef}
            passenger={nonCancelledSubscription?.passenger}
            name={nonCancelledSubscription?.name}
            travelDate={nonCancelledSubscription?.travelDate}
            currency={nonCancelledSubscription?.currency}
            price={nonCancelledSubscription?.price}
            bookingReference={subscription.bookingReference}
            bookingWithin14DaysCoolingOffPeriod={bookingWithin14DaysCoolingOffPeriod}
            travelPassReference={subscription?.travelPassReference}
            membershipNumber={customerInfo.profile?.loyalty?.membershipNumber}
            customerId={customerInfo?.profile?.customerId}
            userCanCancelSubscription={userCanCancelSubscription}
            isThalysAdminUserOffice={isThalysAdminUserOffice}
            intl={intl}
            startDate={subscription.startDate}
            endDate={subscription.endDate}
            confirmedTimestamp={subscription.booking.confirmedTimestamp}
          />
        </Modal>
      )}

      <StyledRow columns={12}>
        <Column span={12}>
          <h4>
            <FormattedMessage id="subscriptions.paymentDetails" />
          </h4>
          <FormContainer>
            <StyledRow style={{ paddingLeft: 16, paddingRight: 16 }} columns={subscription?.booking?.expiryTimestamp ? 18 : 15}>
              <Column span={3}>
                <StyledLabel htmlFor="price">
                  <FormattedMessage id="subscriptions.price" />
                </StyledLabel>
                <StyledDescription data-cy="subscription-price">
                  <CurrencySymbol currencyCode={subscription?.booking?.subscriptions[0].currency} />{' '}
                  {parseFloat(subscription?.booking?.subscriptions[0].price).toFixed(2)}
                </StyledDescription>
              </Column>
              <Column span={3}>
                <StyledLabel htmlFor="discount-code">
                  <FormattedMessage id="clubEurostar.discountCodeApplied" />
                </StyledLabel>
                <StyledDescription>
                  {discountCode ? (
                    <Link to={`/discountCodes/${discountCode}`} target="_blank">{`${discountCode.slice(0, 3)} ....`}</Link>
                  ) : (
                    '-'
                  )}
                </StyledDescription>
              </Column>
              <Column span={3}>
                <>
                  <StyledLabel htmlFor="type-of-use">
                    <FormattedMessage id="subscriptions.typeOfUse" />
                  </StyledLabel>
                  <StyledDescription data-cy="subscription-type-of-use">
                    {typeOfUse ? <FormattedMessage id={`thalyspass.${typeOfUse}`} /> : '-'}
                  </StyledDescription>
                </>
              </Column>
              <Column span={3}>
                <>
                  <StyledLabel htmlFor="vat-status">
                    <FormattedMessage id="subscriptions.vatStatus" />
                  </StyledLabel>
                  <StyledDescription data-cy="subscription-vat-status">{vatStatusValue}</StyledDescription>
                </>
              </Column>
              <Column span={3}>
                <StyledLabel htmlFor="vat">
                  <FormattedMessage id="subscriptions.vat" />
                </StyledLabel>
                <StyledDescription data-cy="subscription-vat">{vatNumber}</StyledDescription>
              </Column>
              {subscription?.booking?.expiryTimestamp && (
                <Column span={3}>
                  <>
                    <StyledLabel htmlFor="vat">
                      <FormattedMessage id="subscriptions.transferDueDate" />
                    </StyledLabel>
                    <span style={{ color: 'Red' }} data-for="subscriptions-transfer-tooltip" data-tip={getTransferDueDateTooltipData()}>
                      {moment(subscription?.booking?.expiryTimestamp).format(DATE_TIME_DISPLAY_FORMAT)}
                    </span>
                    <ReactTooltip id="subscriptions-transfer-tooltip" data-cy="subscriptions-transfer-tooltip" html={true} />
                  </>
                </Column>
              )}
            </StyledRow>
            <PaymentsSummary
              payments={subscription?.booking?.payments || []}
              bookingReference={subscription?.bookingReference}
              isStationUser={false}
            />
          </FormContainer>
        </Column>
      </StyledRow>
      <StyledRow columns={12}>
        <Column span={12}>
          <Bookings
            customerId={customerInfo?.profile?.customerId}
            travelPassReference={subscription?.travelPassReference}
            membershipNumber={customerInfo.profile?.loyalty?.membershipNumber}
            code={subscription.code}
          />
        </Column>
      </StyledRow>
    </>
  );
};

SubscriptionDetails.propTypes = {
  subscriptionData: PropTypes.arrayOf(
    PropTypes.shape({
      booking: bookingShape,
      bookingReference: PropTypes.string,
      travelPassReference: PropTypes.string,
      code: PropTypes.string,
      status: PropTypes.string,
      startDate: PropTypes.string,
      endDate: PropTypes.string
    })
  ),
  intl: PropTypes.shape({ formatMessage: PropTypes.func }),
  history: historyShape,
  customerInfo: customerInfoShape,
  user: userShape,
  canSellSubscription: PropTypes.bool
};

export default withRouter(injectIntl(SubscriptionDetails));
